import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { getListProjectsQueryKey } from "~/api/folders/folders";
import { useDeleteProject, useGetProject } from "~/api/projects/projects";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";

interface DeleteCaseAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
}

export const DeleteCaseAlertDialog: FC<DeleteCaseAlertDialogProps> = ({
  open,
  setOpen,
  projectId,
}) => {
  const { organizationId } = useOrganizationParams();
  const { data } = useGetProject(organizationId, projectId);
  const { mutate } = useDeleteProject();
  const queryClient = useQueryClient();

  const onDeleteCase = async () => {
    mutate(
      { organizationId, projectId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getListProjectsQueryKey(organizationId),
          });
          setOpen(false);
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="casesList.deleteCaseAlertDialog.title"
              defaultMessage="Delete Case"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="casesList.deleteCaseAlertDialog.description"
              defaultMessage={
                '{caseName} will be deleted, this action cannot be undone. All uploaded files will be moved to "Deleted Documents" in your account settings{caseName}'
              }
              values={{
                // TODO: add skeleton in case the data is not loaded yet
                caseName: <strong>{data?.name}</strong>,
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button variant="destructive" onClick={onDeleteCase}>
            <FormattedMessage id="continue" defaultMessage="Continue" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
